import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from "@mui/material";
import CommonButton from "@/views/CommonComponent/CommonButton";
import {
  InfoBox,
  SignInContentsBox,
  SignInRoot,
  PlainInputWrapper,
  PlainInputField,
  PlainInputRightSlot,
  InputGroup,
  InputLabelText,
  InputLabelRequired,
  GenderButtonGroup,
  GenderButton,
  NoteText,
  ErrorText,
  ButtonContainer,
} from "./Styled/SignInStyled";
import TitleBox from "../CommonComponent/TitleBox";
import {
  SignInProgressBarContainer,
  SignInProgressTrack,
  SignInProgressFill,
} from "./Styled/SignInStyled";
import { inject, observer } from "mobx-react";
import {useHistory, useLocation} from "react-router-dom";
import CommonDialog from "@/views/CommonComponent/CommonDialog";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { useToast } from "../../components/useToast";

const PendingProfileApplyToastKey = "_PENDING_PROFILE_APPLY_TOAST_";

const ConsentBadge = styled(Chip)`
  margin-left: 8px;
  background-color: #868aff;
  color: white;
  font-size: 12px;
  height: 24px;
`;

const BirthDateHelperText = React.memo(function BirthDateHelperText(props) {
  const { lines } = props;
  const location = useLocation();

  return (
    <Box
      component="ul"
      sx={{
        margin: 0,
        marginTop: 0.5,
        listStyleType: "disc",
        paddingLeft: 2.5,
        color: "#8F8F8F",
        fontSize: "0.75rem", 
      }}
    >
      {lines.map((line) => (
        <Box component="li" key={line}>
           {line.replace(/^\*\s*/, "")}
        </Box>
      ))}
    </Box>
  );
});

function SignInInfo(props) {
  const { intl, authStore } = props;
  const history = useHistory();
  const location = history.location;
  const { showDeleteToast } = useToast();

  const [open, setOpen] = useState(false);
  const [isNicknameTouched, setIsNicknameTouched] = useState(false);
  const [isPenNameTouched, setIsPenNameTouched] = useState(false);
  const [ageOver14, setAgeOver14] = useState('');

  useEffect(() => {
    if (location.state?.ageOver14 !== undefined) {
      setAgeOver14(location.state.ageOver14);
      authStore.setAgeOver14(location.state.ageOver14);
    }
    if (location.state?.ageAgreedToUseUnder14 !== undefined) {
      authStore.setAgeAgreedToUseUnder14(location.state.ageAgreedToUseUnder14);
    }
  }, [location.state]);

  useEffect(() => {
    if (localStorage.getItem(PendingProfileApplyToastKey) === null) {
      return;
    }
    showDeleteToast(localStorage.getItem(PendingProfileApplyToastKey));
    localStorage.removeItem(PendingProfileApplyToastKey);
  }, [intl, showDeleteToast]);

  const handleInput = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
  };


  const handleChangeNickname = (e) => {
    const value = e.target.value;
    setIsNicknameTouched(true);
    if (value.length <= 16) {
      authStore.setNickname(value);
    }
  };

  const handleChangePenName = (e) => {
    const value = e.target.value;
    setIsPenNameTouched(true);
    if (value.length <= 16) {
      authStore.setPenName(value);
    }
  };

  const handleBlurNickname = () => {
    setIsNicknameTouched(true);
  };

  const handleBlurPenName = () => {
    setIsPenNameTouched(true);
  };

  const handleChangeBirthDate = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");

    // 생년월일은 숫자 8자리까지만 허용
    if (value.length > 8) {
      return;
    }

    // 생년월일을 수정하면, 이전에 판정/동의된 만14세 여부 상태는 더 이상 유효하지 않으므로 초기화
    if (authStore.ageAgreedToUseUnder14 !== "" || authStore.ageOver14 !== "") {
      authStore.setAgeAgreedToUseUnder14("");
      authStore.setAgeOver14("");
    }

    authStore.setBirthDate(value);
  };

  const handleSelectBoy = () => {
    if (authStore.gender === "boy") {
      authStore.setGender("");
      return;
    }
    authStore.setGender("boy");
  };

  const handleSelectGirl = () => {
    if (authStore.gender === "girl") {
      authStore.setGender("");
      return;
    }
    authStore.setGender("girl");
  };

  const handleClick = () => {
    if (isUnder14(authStore.birthDate)) {
      authStore.setAgeOver14(false);
    } else {
      authStore.setAgeOver14(true);
    }

    if (authStore.ageAgreedToUseUnder14) {
      history.push("/signin/memory-consent", {
        notNewUser: location.state?.notNewUser,
        redirectUrl: location.state?.redirectUrl,
        deviceId: location.state?.deviceId,
        returnToPokoroSettingAfterProfile:
          location.state?.returnToPokoroSettingAfterProfile,
      });
    } else {
      history.push("/signin/guardian-consent", {
        notNewUser: location.state?.notNewUser,
        redirectUrl: location.state?.redirectUrl,
        deviceId: location.state?.deviceId,
        returnToPokoroSettingAfterProfile:
          location.state?.returnToPokoroSettingAfterProfile,
      });
    }
  };

  const handleClickPrevious = () => {
    history.push("/signin/terms", { notNewUser: location.state?.notNewUser });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClickClose = () => {
    setOpen(false);
  };

  const moveLoginPage = () => {
    authStore.doLogout(() => history.push("/"));
  };

  const isBirthDateValid = (date) => {
    if (date === '') {
      return true;
    }

    if (typeof date === 'number') {
      date = date.toString();
    }
    // 정규 표현식으로 기본 형식 및 년도 범위 검사 (1900-1999, 2000-2099)
    if (!/^(19\d{2}|20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/.test(date)) {
      return false;
    }

    // 연도, 월, 일을 분리
    const year = parseInt(date.substring(0, 4), 10);
    const month = parseInt(date.substring(4, 6), 10) - 1; // 월은 0부터 시작
    const day = parseInt(date.substring(6, 8), 10);

    // Date 객체를 사용하여 유효한 날짜인지 확인
    const birthDate = new Date(year, month, day);
    return (
      birthDate.getFullYear() === year &&
      birthDate.getMonth() === month &&
      birthDate.getDate() === day
    );
  };

  const isUnder14 = (birthDate) => {
    if (!birthDate || birthDate.length !== 8) return false;
    
    const year = parseInt(birthDate.substring(0, 4));
    const month = parseInt(birthDate.substring(4, 6));
    const day = parseInt(birthDate.substring(6, 8));
    
    const birth = new Date(year, month - 1, day);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1 < 14;
    }
    
    return age < 14;
  };

  const isNextButtonDisabled = () => {
    const nicknameValue = authStore.nickname || "";
    const penNameValue = authStore.penName || "";
    const birthDateValue = authStore.birthDate || "";

    return (
      authStore.isNotAcceptableNickname(intl, nicknameValue) !== "" ||
      nicknameValue.trim() === "" ||
      (penNameValue.length !== 0 &&
        authStore.isNotAcceptablePenName(intl, penNameValue) !== "") ||
      birthDateValue.trim() === "" ||
      !isBirthDateValid(birthDateValue)
    );
  };

  // 만 14세 기준일 계산 함수
  const getUnder14Date = () => {
    const today = new Date();
    const year = today.getFullYear() - 14;
    const month = today.getMonth() + 1; // 1~12월
    const day = today.getDate();

    return {
      year: year,
      month: month,
      day: day
    }
  };

  const birthDateHelperLines = intl
    .formatMessage({ id: "birthDate_note" })
    .split("\n")
    .filter(Boolean);

  const nicknameValidationMessage = authStore.isNotAcceptableNickname(intl, authStore.nickname);
  const penNameValidationMessage = authStore.isNotAcceptablePenName(intl, authStore.penName);

  const showNicknameError =
    isNicknameTouched && ((authStore.nickname || "").trim() === "" || nicknameValidationMessage !== "");
  const showPenNameError =
    isPenNameTouched && (authStore.penName || "") !== "" && penNameValidationMessage !== "";

  const InfoProgressBar = React.memo(function InfoProgressBarComponent(props) {
    const { percent } = props;

    return (
      <SignInProgressBarContainer aria-label="회원가입 진행률">
        <SignInProgressTrack role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <SignInProgressFill percent={percent} />
        </SignInProgressTrack>
      </SignInProgressBarContainer>
    );
  });

  return (
    <SignInRoot>
      <TitleBox
        title={intl.formatMessage({ id: "signIn_Title" })} /* 포코로와 대화할 이용자의 정보를 입력해 주세요. */
        subtitle={intl.formatMessage({ id: "signIn_Text" })} /* 입력된 정보를 바탕으로 맞춤 대화를 제공해요. */
        showBackButton={authStore.loginUser.profileExists && (location.state?.showBackButton ?? true)}
      />
      {!location.state?.notNewUser && <InfoProgressBar percent={66} />}
      <SignInContentsBox>
        <InfoBox mt={1}>
          <InputGroup>
            <InputLabelText>
              {intl.formatMessage({ id: "name_label" })} {/* 이름 */}
              <InputLabelRequired>*</InputLabelRequired>
            </InputLabelText>
            <PlainInputField
              placeholder={intl.formatMessage({ id: "name_placeholder" })} /* 이름(16자이내, 특수문자 제외) */
              value={authStore.nickname}
              onChange={handleChangeNickname}
              onBlur={handleBlurNickname}
              autoComplete="off"
              hasError={showNicknameError}
            />
            {showNicknameError && (
              <ErrorText>{nicknameValidationMessage}</ErrorText>
            )}
          </InputGroup>
          <InputGroup>
            <InputLabelText>
              {intl.formatMessage({ id: "birthDate_label" })} {/* 생년월일 */}
              <InputLabelRequired>*</InputLabelRequired>
            </InputLabelText>
            <PlainInputWrapper>
              <PlainInputField
                type="tel"
                onInput={handleInput}
                placeholder={intl.formatMessage({ id: "birthDate_placeholder" })} /* YYYYMMDD */
                value={authStore.birthDate}
                onChange={handleChangeBirthDate}
                autoComplete="off"
                // disabled={authStore.ageAgreedToUseUnder14 === true}
                hasError={!isBirthDateValid(authStore.birthDate)}
              />
              {authStore.ageAgreedToUseUnder14 === true && (
                <PlainInputRightSlot>
                  <ConsentBadge
                    label={intl.formatMessage({
                      id: authStore.ageOver14 === true
                        ? "guardian_consent_complete_badge_over14"
                        : "guardian_consent_complete_badge",
                    })}
                    size="small"
                  />
                </PlainInputRightSlot>
              )}
            </PlainInputWrapper>
            <Box mt={0.5}>
              <BirthDateHelperText lines={birthDateHelperLines} />
            </Box>
          </InputGroup>
          <InputGroup>
            <InputLabelText>{intl.formatMessage({ id: "gender_label" })}</InputLabelText>
            <GenderButtonGroup>
              <GenderButton
                type="button"
                isSelected={authStore.gender === "boy"}
                onClick={handleSelectBoy}
              >
                {intl.formatMessage({ id: "gender_boy" })}
              </GenderButton>
              <GenderButton
                type="button"
                isSelected={authStore.gender === "girl"}
                onClick={handleSelectGirl}
              >
                {intl.formatMessage({ id: "gender_girl" })}
              </GenderButton>
            </GenderButtonGroup>
          </InputGroup>
          <InputGroup>
            <InputLabelText>{intl.formatMessage({ id: "penName_label" })} {/* 펜 이름 (선택) */} </InputLabelText>
            <PlainInputField
              placeholder={intl.formatMessage({ id: "name_placeholder" })} /* 펜 이름(16자이내, 특수문자 제외) */
              value={authStore.penName}
              onChange={handleChangePenName}
              onBlur={handleBlurPenName}
              autoComplete="off"
              hasError={showPenNameError}
            />
            {showPenNameError && (
              <ErrorText>{penNameValidationMessage}</ErrorText>
            )}
          </InputGroup>
        </InfoBox>
        <ButtonContainer>
          {!location.state?.notNewUser &&
              <CommonButton
                  text={intl.formatMessage({ id: "signIn_previous" })}
                  background="#fff"
                  border="#C9C9CA"
                  color="#8f8f8f"
                  onClick={handleClickPrevious}
              />
          }
          <CommonButton
            text={intl.formatMessage({ id: "dialog_nextButton" })} /* 다음 */
            background="#FDD751"
            color="#333"
            disabled={isNextButtonDisabled()}
            onClick={handleClick}
          />
        </ButtonContainer>
      </SignInContentsBox>
      <CommonDialog
        description="뒤로가기(로그인) 팝업"
        open={open}
        title={intl.formatMessage({ id: "signInfo_DialogTitle" })} /* 로그인 페이지로 이동하시겠습니까? */
        subText={<pre>{intl.formatMessage({ id: "signIn_DialogSubText" })}</pre>}
        onClick1={moveLoginPage}
        onClick2={handleClickClose}
        onClose={() => {}} // 배경 클릭 시 아무 동작하지 않도록 설정
        LeftButtonText={intl.formatMessage({ id: "dialog_YesButton" })}
        RightButtonText={intl.formatMessage({ id: "dialog_NoButton" })}
      />
    </SignInRoot>
  );
}

export default injectIntl(inject("authStore")(observer(SignInInfo)));
